import { show as showLoadingMask, hide as hideLoadingMask } from './load-mask.js';
import { csrfFetch, csrfFetchJsonWithFeedback, showFetchErrorDialog } from './utils/csrf-fetch.js';

async function depositCampaignCode(campaignCode) {
  showLoadingMask();
  const formData = new FormData();
  formData.append('campaignCode', campaignCode);

  let response;
  try {
    response = await csrfFetchJsonWithFeedback('/basketActionEndpoint.php?action=depositCampaignCode', {
      method: 'POST',
      body: formData,
    });
  } catch {
    hideLoadingMask();
    return;
  }

  if (!response.success) {
    showFetchErrorDialog(response.error);
    hideLoadingMask();
    return;
  }

  window.location.reload();
}

async function removeCampaignCode() {
  showLoadingMask();

  // The endpoint answers with an empty body, so only the status tells whether the coupon is gone.
  const response = await csrfFetch('/shopController.php?objective=removeCouponCodes', {
    method: 'POST',
  }).catch(() => null);

  if (!response?.ok) {
    showFetchErrorDialog();
    hideLoadingMask();
    return;
  }

  window.location.reload();
}

/**
 * Reads the coupon's allowed payment method ids from the payment gateway container.
 */
function getCouponAllowedPaymentMethodIds() {
  const paymentGateway = document.querySelector('[data-coupon-allowed-payments]');
  if (!paymentGateway) {
    return [];
  }
  return JSON.parse(paymentGateway.dataset.couponAllowedPayments || '[]');
}

/**
 * Reads the customer-facing label of a payment option straight from its own markup, so the pending
 * hint stays in sync with whatever the checkout calls that method without a separate id->label map.
 */
function getPaymentMethodLabel(bezahlartId) {
  const option = document.querySelector(`.zahlungsmethode[data-value="${bezahlartId}"]`);
  return option?.querySelector('.radio_container b')?.textContent.trim() ?? '';
}

/**
 * Fills the checkout pending hint with the payment method(s) the deposited campaign coupon actually
 * allows: the qualifying method label(s) in the copy and the CTA that jumps to the first one.
 */
function fillCampaignPendingHint() {
  const pendingHint = document.querySelector('.js_campaign_pending');
  if (!pendingHint) {
    return;
  }

  const renderedAllowedMethods = getCouponAllowedPaymentMethodIds()
    .map((bezahlartId) => ({ bezahlartId, label: getPaymentMethodLabel(bezahlartId) }))
    .filter((method) => method.label !== '');
  if (renderedAllowedMethods.length === 0) {
    return;
  }

  const methodElement = pendingHint.querySelector('.js_campaign_pending_method');
  if (methodElement) {
    methodElement.textContent = renderedAllowedMethods.map((method) => method.label).join(' oder ');
  }

  const selectButton = pendingHint.querySelector('.js_campaign_select_payment');
  if (selectButton) {
    const [primaryMethod] = renderedAllowedMethods;
    selectButton.textContent = `${primaryMethod.label} wählen und sparen`;
    selectButton.dataset.paymentId = String(primaryMethod.bezahlartId);
  }
}

/**
 * Handle clicks on campaign notification buttons via event delegation, so the
 * listeners survive AJAX re-renders of the surrounding markup.
 */
function addCampaignNotificationListeners() {
  fillCampaignPendingHint();

  document.addEventListener('click', (event) => {
    const redeemButton = event.target.closest('.js_campaign_redeem');
    if (redeemButton) {
      depositCampaignCode(redeemButton.dataset.campaignCode);
      return;
    }

    const removeButton = event.target.closest('.js_campaign_remove');
    if (removeButton) {
      removeCampaignCode();
      return;
    }

    // Checkout pending hint: jump the customer to a qualifying payment option so the discount applies.
    const selectPaymentButton = event.target.closest('.js_campaign_select_payment');
    if (selectPaymentButton) {
      const paymentOption = document.querySelector(
        `.zahlungsmethode[data-value="${selectPaymentButton.dataset.paymentId}"]`,
      );
      paymentOption?.click();
    }
  });
}

export { addCampaignNotificationListeners };
